import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, Maximize2 } from 'lucide-react';
import { openURL } from '@/utils/navigationUtils';
import { decodeText } from '@/utils/textDecoding';

interface YouTubeVideoViewerProps {
  videoUrl: string;
  buttonText: string;
  detectVertical?: boolean;
}

const YouTubeVideoViewer: React.FC<YouTubeVideoViewerProps> = ({ videoUrl, buttonText, detectVertical = false }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isVertical, setIsVertical] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Ensure videoUrl is a string and clean YouTube URLs by removing &feature=youtu.be parameter
  const videoUrlString = typeof videoUrl === 'string' ? videoUrl : String(videoUrl || '');
  const cleanedVideoUrl = videoUrlString.replace(/&feature=youtu\.be/g, '');
  
  // Fix encoding issues in button text (e.g., "VidÃ©o" -> "Vidéo")
  const decodedButtonText = decodeText(buttonText);

  // Extract YouTube video ID from various URL formats
  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  const videoId = extractVideoId(cleanedVideoUrl);

  if (!videoId) {
    return null;
  }

  // Fetch video metadata to detect vertical videos
  useEffect(() => {
    if (!detectVertical || !videoId) return;

    const fetchVideoMetadata = async () => {
      try {
        const response = await fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`);
        if (response.ok) {
          const data = await response.json();
          const { width, height } = data;
          // Consider vertical if height is greater than width
          setIsVertical(height > width);
        }
      } catch (error) {
        console.error('Failed to fetch video metadata:', error);
      }
    };

    fetchVideoMetadata();
  }, [videoId, detectVertical]);

  // YouTube embed URL with autoplay, muted, no controls, and infinite loop with captions enabled
  // Parameters to minimize branding: modestbranding=1, fs=0 (hide fullscreen), iv_load_policy=3 (hide annotations), disablekb=1 (disable keyboard)
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&fs=0&iv_load_policy=3&disablekb=1&loop=1&playlist=${videoId}&cc_load_policy=1`;
  const fullscreenEmbedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=0&controls=0&rel=0&modestbranding=1&fs=0&iv_load_policy=3&disablekb=1&loop=1&playlist=${videoId}&cc_load_policy=1`;

  const handleVideoClick = () => {
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  // Use vertical aspect ratio for vertical videos, standard 16:9 otherwise
  const aspectRatioPadding = isVertical ? 'pb-[177.78%]' : 'pb-[56.25%]'; // 9:16 vs 16:9

  return (
    <div className="my-3 w-full max-w-full overflow-hidden">
      {/* Embedded video player */}
      <div className="relative w-full">
        <div 
          className={`relative w-full h-0 ${aspectRatioPadding} rounded-lg overflow-hidden cursor-pointer group`}
          onClick={handleVideoClick}
        >
          <iframe
            ref={iframeRef}
            src={embedUrl}
            className="absolute top-0 left-0 w-full h-full d2cbot-bot_border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={decodedButtonText}
          />
          {/* Overlay for click detection and fullscreen icon */}
          <div className="absolute inset-0 bg-transparent group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center">
            <div className="bg-black/50 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <Maximize2 className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>


      {/* Fullscreen modal */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-7xl w-full h-[90vh] p-0 bg-black">
          <div className="relative w-full h-full">
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70"
              onClick={closeFullscreen}
            >
              <X className="w-4 h-4" />
            </Button>
            
            {/* Fullscreen video */}
            <iframe
              src={fullscreenEmbedUrl}
              className="w-full h-full d2cbot-bot_border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={decodedButtonText}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YouTubeVideoViewer;